import React, { useState, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { useQuery } from 'react-query';
import { leadService, userService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  DocumentArrowUpIcon,
} from '@heroicons/react/24/outline';

const ROLE_LABELS = { super_admin: 'Super Admin', admin: 'Admin', dealer: 'Dealer', sales_executive: 'Sales Executive' };

// Column definitions for template + preview
const COLUMNS = [
  { key: 'Name',            required: true,  note: 'Customer full name' },
  { key: 'Phone',           required: true,  note: '10-digit mobile number' },
  { key: 'Alternate Phone', required: false, note: '' },
  { key: 'Email',           required: false, note: '' },
  { key: 'State',           required: false, note: '' },
  { key: 'City',            required: false, note: '' },
  { key: 'Address',         required: false, note: '' },
  { key: 'Brand',           required: false, note: 'e.g. Maruti, Hyundai' },
  { key: 'Model',           required: false, note: 'e.g. Swift, Creta' },
  { key: 'Buying Time',     required: false, note: 'immediate / within_1_month / within_3_months / within_6_months / just_enquiring' },
  { key: 'Source',          required: false, note: 'website / phone / email / social_media / facebook / referral / walk_in / other' },
  { key: 'Priority',        required: false, note: 'low / medium / high / urgent' },
  { key: 'Enquiry Date',    required: false, note: 'YYYY-MM-DD' },
  { key: 'Notes',           required: false, note: '' },
  { key: 'User ID',         required: false, note: 'Sends this row to a specific user — the User ID shown in the Users list (e.g. DLR-0007). Overrides the target picked below, per row' },
];

const SAMPLE_ROWS = [
  {
    Name: 'Rahul Sharma', Phone: '9876543210', 'Alternate Phone': '', Email: 'rahul@example.com',
    State: 'Maharashtra', City: 'Mumbai', Address: '123 Main St', Brand: 'Maruti', Model: 'Swift',
    'Buying Time': 'immediate', Source: 'walk_in', Priority: 'high', 'Enquiry Date': '2026-05-07', Notes: 'Interested in test drive',
    'User ID': 'DLR-0001',
  },
  {
    Name: 'Priya Patel', Phone: '9123456780', 'Alternate Phone': '9123456781', Email: '',
    State: 'Gujarat', City: 'Ahmedabad', Address: '', Brand: 'Hyundai', Model: 'Creta',
    'Buying Time': 'within_1_month', Source: 'phone', Priority: 'medium', 'Enquiry Date': '2026-05-07', Notes: '',
    'User ID': 'DLR-0002',
  },
];

function downloadTemplate() {
  const ws = XLSX.utils.json_to_sheet(SAMPLE_ROWS, { header: COLUMNS.map(c => c.key) });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Leads');
  XLSX.writeFile(wb, 'bulk_leads_template.xlsx');
}

export default function BulkUploadModal({ onClose, onSuccess }) {
  const { user } = useAuth();
  const [file, setFile]               = useState(null);
  const [preview, setPreview]         = useState([]);
  const [uploading, setUploading]     = useState(false);
  const [result, setResult]           = useState(null);
  const [dragOver, setDragOver]       = useState(false);
  // Cascading selection (final assignedTo = the lowest selected level)
  const [selectedAdminId,    setSelectedAdminId]    = useState('');
  const [selectedDealerId,   setSelectedDealerId]   = useState('');
  const [selectedSalesExecId, setSelectedSalesExecId] = useState('');
  const fileInputRef = useRef();

  const myRole = user?.role;
  const isSuperAdmin = myRole === 'super_admin';
  const isAdmin      = myRole === 'admin';
  const isDealer     = myRole === 'dealer';

  // Fetch downline users so the uploader can assign the batch to someone else.
  const { data: usersData } = useQuery(
    'bulkUploadAssignableUsers',
    () => userService.getUsers({ status: 'active', limit: 300 }),
    { staleTime: 5 * 60_000 }
  );
  const allUsers = (() => {
    const d = usersData?.data;
    return d?.users || d?.data?.users || [];
  })();

  const myId = String(user?._id || user?.id || '');

  const getParentId = (u) => {
    const pid = typeof u.parentId === 'object' ? u.parentId?._id : u.parentId;
    return pid ? String(pid) : null;
  };

  // Lists for cascade
  const adminList = useMemo(() =>
    allUsers.filter(u => u.role === 'admin'),
  [allUsers]);

  // Dealers visible to current uploader
  const dealerList = useMemo(() => {
    const dealers = allUsers.filter(u => u.role === 'dealer');
    if (isSuperAdmin) {
      if (!selectedAdminId) return [];
      return dealers.filter(d => getParentId(d) === selectedAdminId);
    }
    if (isAdmin) {
      return dealers.filter(d => getParentId(d) === myId);
    }
    return [];
  }, [allUsers, isSuperAdmin, isAdmin, selectedAdminId, myId]);

  // Sales execs visible to current uploader
  const salesExecList = useMemo(() => {
    const execs = allUsers.filter(u => u.role === 'sales_executive');
    if (selectedDealerId) {
      return execs.filter(se => getParentId(se) === selectedDealerId);
    }
    if (isDealer) {
      return execs.filter(se => getParentId(se) === myId);
    }
    return [];
  }, [allUsers, selectedDealerId, isDealer, myId]);

  // Resolve final assignedTo from cascade
  const assignedTo =
    selectedSalesExecId ||
    selectedDealerId    ||
    selectedAdminId     ||
    '';

  const parsePreview = (f) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb   = XLSX.read(e.target.result, { type: 'binary', cellDates: true });
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
        setPreview(rows.slice(0, 5));
      } catch {
        setPreview([]);
      }
    };
    reader.readAsBinaryString(f);
  };

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      toast.error('Only CSV and Excel (.xlsx/.xls) files are allowed');
      return;
    }
    setFile(f);
    setResult(null);
    parsePreview(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await leadService.bulkUpload(file, assignedTo);
      setResult(res.data.data);
      if (res.data.data.success > 0) {
        toast.success(`${res.data.data.success} leads uploaded successfully!`);
        onSuccess && onSuccess();
      }
      if (res.data.data.skipped?.length > 0) {
        toast(`${res.data.data.skipped.length} duplicate rows skipped`);
      }
      if (res.data.data.failed > 0) {
        toast.error(`${res.data.data.failed} rows had errors`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-40" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-2">
              <DocumentArrowUpIcon className="h-5 w-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">Bulk Upload Leads</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 space-y-5 overflow-y-auto flex-1">
            {/* Download template */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-blue-50 rounded-lg px-4 py-3">
              <div>
                <p className="text-sm font-medium text-blue-800">Download Template</p>
                <p className="text-xs text-blue-600 mt-0.5">Fill in your leads using the correct format</p>
              </div>
              <button onClick={downloadTemplate}
                className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 border border-blue-300 rounded-lg px-3 py-1.5 hover:bg-blue-100">
                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                Template.xlsx
              </button>
            </div>

            {/* Assign To cascading picker */}
            {!result && (
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700">Default Target for Uploaded Leads</label>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Defaults to <span className="font-medium">Myself</span>. Used for any row that doesn't have a <span className="font-medium">User ID</span> — add that column to send different rows to different people in one upload.
                  </p>
                </div>

                {/* Super Admin: Myself / Admin */}
                {isSuperAdmin && (
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 mb-1">Admin / Self</label>
                    <select
                      value={selectedAdminId || '__self'}
                      onChange={e => {
                        const v = e.target.value;
                        if (v === '__self') {
                          setSelectedAdminId(''); setSelectedDealerId(''); setSelectedSalesExecId('');
                        } else {
                          setSelectedAdminId(v); setSelectedDealerId(''); setSelectedSalesExecId('');
                        }
                      }}
                      className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="__self">— Myself ({user?.firstName} {user?.lastName}) —</option>
                      {adminList.map(a => (
                        <option key={a._id} value={a._id}>{a.firstName} {a.lastName} (Admin)</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Admin: Myself / Dealer */}
                {isAdmin && (
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 mb-1">Dealer / Self</label>
                    <select
                      value={selectedDealerId || '__self'}
                      onChange={e => {
                        const v = e.target.value;
                        if (v === '__self') {
                          setSelectedDealerId(''); setSelectedSalesExecId('');
                        } else {
                          setSelectedDealerId(v); setSelectedSalesExecId('');
                        }
                      }}
                      className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="__self">— Myself ({user?.firstName} {user?.lastName}) —</option>
                      {dealerList.map(d => (
                        <option key={d._id} value={d._id}>{d.firstName} {d.lastName} (Dealer)</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Super Admin Step-2: pick a Dealer under selected Admin (optional) */}
                {isSuperAdmin && selectedAdminId && (
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 mb-1">Dealer (optional)</label>
                    <select
                      value={selectedDealerId}
                      onChange={e => { setSelectedDealerId(e.target.value); setSelectedSalesExecId(''); }}
                      className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">— Keep with Admin —</option>
                      {dealerList.map(d => (
                        <option key={d._id} value={d._id}>{d.firstName} {d.lastName} (Dealer)</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Sales Exec dropdown — appears once a Dealer is selected (or uploader is dealer) */}
                {(selectedDealerId || isDealer) && salesExecList.length > 0 && (
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 mb-1">
                      {isDealer ? 'Sales Executive / Self' : 'Sales Executive (optional)'}
                    </label>
                    <select
                      value={selectedSalesExecId || (isDealer ? '__self' : '')}
                      onChange={e => {
                        const v = e.target.value;
                        if (v === '__self') setSelectedSalesExecId('');
                        else setSelectedSalesExecId(v);
                      }}
                      className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {isDealer
                        ? <option value="__self">— Myself ({user?.firstName} {user?.lastName}) —</option>
                        : <option value="">— Keep with Dealer —</option>
                      }
                      {salesExecList.map(se => (
                        <option key={se._id} value={se._id}>
                          {se.firstName} {se.lastName} (Sales Executive)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Summary line */}
                <div className="text-[11px] text-gray-500 bg-gray-50 rounded px-3 py-1.5">
                  {(() => {
                    const lookup = (id) => allUsers.find(u => String(u._id) === String(id));
                    const target = assignedTo ? lookup(assignedTo) : null;
                    if (!target) return <>Final target: <span className="font-medium text-gray-700">Myself ({user?.firstName} {user?.lastName})</span></>;
                    return <>Final target: <span className="font-medium text-gray-700">{target.firstName} {target.lastName} ({ROLE_LABELS[target.role] || target.role})</span></>;
                  })()}
                </div>
              </div>
            )}

            {/* Drop zone */}
            {!result && (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
                  ${dragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'}`}
              >
                <ArrowUpTrayIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                {file ? (
                  <p className="text-sm font-medium text-gray-700">{file.name}
                    <span className="ml-2 text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                  </p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-700">Drop your file here or click to browse</p>
                    <p className="text-xs text-gray-400 mt-1">Supports CSV, XLSX, XLS — max 5 MB</p>
                  </>
                )}
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
                  onChange={(e) => handleFile(e.target.files[0])} />
              </div>
            )}

            {/* Preview table */}
            {preview.length > 0 && !result && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Preview (first {preview.length} rows)
                </p>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="text-xs w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        {Object.keys(preview[0]).slice(0, 6).map(col => (
                          <th key={col} className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">{col}</th>
                        ))}
                        {Object.keys(preview[0]).length > 6 && <th className="px-3 py-2 text-gray-400">+more</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((row, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          {Object.values(row).slice(0, 6).map((val, j) => (
                            <td key={j} className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-[120px] truncate">{String(val)}</td>
                          ))}
                          {Object.keys(row).length > 6 && <td className="px-3 py-2 text-gray-400">…</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Result summary */}
            {result && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-800">{result.total}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Total Rows</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{result.success}</p>
                    <p className="text-xs text-green-600 mt-0.5">Created</p>
                  </div>
                  <div className={`rounded-lg p-3 text-center ${result.skipped?.length > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
                    <p className={`text-2xl font-bold ${result.skipped?.length > 0 ? 'text-amber-600' : 'text-gray-400'}`}>{result.skipped?.length || 0}</p>
                    <p className={`text-xs mt-0.5 ${result.skipped?.length > 0 ? 'text-amber-600' : 'text-gray-400'}`}>Skipped</p>
                  </div>
                  <div className={`rounded-lg p-3 text-center ${result.failed > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                    <p className={`text-2xl font-bold ${result.failed > 0 ? 'text-red-600' : 'text-gray-400'}`}>{result.failed}</p>
                    <p className={`text-xs mt-0.5 ${result.failed > 0 ? 'text-red-500' : 'text-gray-400'}`}>Failed</p>
                  </div>
                </div>

                {result.errors.length > 0 && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 max-h-48 overflow-y-auto">
                    <p className="text-xs font-semibold text-red-700 mb-2">Row Errors:</p>
                    {result.errors.map((e, i) => (
                      <div key={i} className="text-xs text-red-600 mb-1">
                        <span className="font-medium">Row {e.row} ({e.name}):</span> {e.errors.join(', ')}
                      </div>
                    ))}
                  </div>
                )}

                {result.skipped?.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 max-h-48 overflow-y-auto">
                    <p className="text-xs font-semibold text-amber-700 mb-2">Skipped as duplicates:</p>
                    {result.skipped.map((s, i) => (
                      <div key={i} className="text-xs text-amber-700 mb-1">
                        <span className="font-medium">Row {s.row} ({s.name}, {s.phone}{s.brand ? `, ${s.brand}` : ''}{s.model ? ` ${s.model}` : ''}):</span> {s.reason}
                      </div>
                    ))}
                  </div>
                )}

                {result.warnings?.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 max-h-48 overflow-y-auto">
                    <p className="text-xs font-semibold text-amber-700 mb-2">Assigned to default target instead:</p>
                    {result.warnings.map((w, i) => (
                      <div key={i} className="text-xs text-amber-700 mb-1">
                        <span className="font-medium">Row {w.row} ({w.name}):</span> {w.message}
                      </div>
                    ))}
                  </div>
                )}

                {result.success > 0 && (
                  <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg px-4 py-2">
                    <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
                    <p className="text-sm">{result.success} leads successfully added to your account.</p>
                  </div>
                )}
              </div>
            )}

            {/* Required columns info */}
            {!result && (
              <div className="bg-amber-50 rounded-lg px-4 py-3">
                <p className="text-xs font-semibold text-amber-800 mb-1">Required columns</p>
                <p className="text-xs text-amber-700">
                  <span className="font-medium">Name</span>, <span className="font-medium">Phone</span> — all other columns are optional.
                  Add <span className="font-medium">User ID</span> (from the Users list, e.g. DLR-0007) to send different rows to different users automatically; leave it blank on a row to use the default target above.
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Rows whose <span className="font-medium">Phone + Brand + Model</span> already exist as a lead, or repeat an earlier row in the sheet, are skipped.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
            {result ? (
              <button onClick={onClose} className="btn-primary text-sm px-6">Done</button>
            ) : (
              <>
                <button onClick={onClose} className="btn-outline-secondary text-sm px-5">Cancel</button>
                <button onClick={handleUpload} disabled={!file || uploading} className="btn-primary text-sm px-6 flex items-center gap-2">
                  {uploading ? (
                    <><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />Uploading...</>
                  ) : (
                    <><ArrowUpTrayIcon className="h-4 w-4" />Upload Leads</>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
